import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Bell,
  BellOff,
  ChevronDown,
  Eye,
  Hash,
  Info,
  Loader2,
  Megaphone,
  MessageSquare,
  Paperclip,
  Plus,
  Search,
  Send,
  Settings,
  Users,
  X,
} from 'lucide-react';

import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { storageFileUrl } from '@/lib/storage-url';
import { aurora, auroraPalette } from '@/components/aurora/tokens';
import { EmptyState } from '@/components/aurora/EmptyState';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Textarea } from '@easyflow/ui-frontend/components/ui/textarea';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@easyflow/ui-frontend/components/ui/sheet';

import { MobileTopBar } from '@/components/mobile/messenger';
import { usePushNavigation } from '@/hooks/usePushNavigation';
import { apiClient, type ApiResponse } from '@/lib/api';

// Task #1722 — Esperienza mobile dedicata per Canali. Master-detail push:
// lista fullscreen → detail fullscreen. Per non-admin il composer è nascosto
// e mostriamo un chip "Solo lettura" subtle nell'header del detail. Per gli
// admin tenant compare il CTA "+" per creare un canale tramite bottom sheet.
// Riusa apiClient (whitelist `/messenger`) e gli endpoint storici
// `/messenger/channels`, `/messenger/conversations/:id/*`,
// `/messenger/messages/:id/comments`. Nessuna modifica backend.

interface ChannelAttachment {
  filename?: string | null;
  name?: string | null;
  mime_type?: string | null;
  type?: string | null;
  cloud_path?: string | null;
  url?: string | null;
  size?: number | null;
}

interface ChannelSummary {
  id: string;
  title: string | null;
  description: string | null;
  is_subscribed?: boolean;
  is_public?: boolean;
  is_official?: boolean;
  created_by?: string | null;
  member_count?: number | null;
  unread_count?: number | null;
  latest_post_at?: string | null;
  last_post_preview?: string | null;
  last_post_title?: string | null;
  updated_at?: string | null;
  created_at?: string | null;
}

interface ChannelDetail extends ChannelSummary {
  my_role?: 'admin' | 'publisher' | 'member' | 'readonly' | null;
}

interface ChannelPost {
  id: string;
  content: string | null;
  attachments?: ChannelAttachment[] | string | null;
  sender_first_name?: string | null;
  sender_last_name?: string | null;
  sender_avatar_url?: string | null;
  created_at: string;
}

interface ChannelComment {
  id: string;
  content: string | null;
  first_name?: string | null;
  last_name?: string | null;
  avatar_url?: string | null;
  created_at: string;
}

type View = 'list' | 'detail';

const AVATAR_GRADIENTS = [
  `${auroraPalette.indigo}`,
  `${auroraPalette.violet}`,
  '#0ea5e9',
  '#10b981',
  '#f59e0b',
  '#ec4899',
];

function gradientFor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  const a = AVATAR_GRADIENTS[Math.abs(hash) % AVATAR_GRADIENTS.length];
  const b = AVATAR_GRADIENTS[Math.abs(hash * 31) % AVATAR_GRADIENTS.length];
  return `linear-gradient(135deg, ${a}, ${b})`;
}

function initialsFor(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p.charAt(0).toUpperCase()).join('').padEnd(1, '·');
}

function formatRelative(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const min = Math.floor((Date.now() - d.getTime()) / 60000);
  if (min < 1) return 'ora';
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}g`;
  try {
    return new Intl.DateTimeFormat('it-IT', { day: '2-digit', month: '2-digit' }).format(d);
  } catch {
    return '';
  }
}

function parseAttachments(att: ChannelPost['attachments']): ChannelAttachment[] {
  if (!att) return [];
  if (Array.isArray(att)) return att;
  try {
    const parsed: unknown = JSON.parse(att);
    return Array.isArray(parsed) ? (parsed as ChannelAttachment[]) : [];
  } catch {
    return [];
  }
}

function attachmentUrl(a: ChannelAttachment): string {
  // Task #4040 — serving autenticato con token in query (asset endpoint).
  return storageFileUrl(a.cloud_path || a.url);
}

// ─────────────────────────────────────────────────────────────────────────
// Lista canali
// ─────────────────────────────────────────────────────────────────────────

interface ChannelListProps {
  channels: ChannelSummary[];
  loading: boolean;
  query: string;
  onQueryChange: (v: string) => void;
  onSelect: (ch: ChannelSummary) => void;
  isAdmin: boolean;
  onCreateClick: () => void;
}

function ChannelListView({
  channels, loading, query, onQueryChange, onSelect, isAdmin, onCreateClick,
}: ChannelListProps) {
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return channels;
    return channels.filter((c) =>
      [c.title, c.description, c.last_post_preview].filter(Boolean).join(' ').toLowerCase().includes(q),
    );
  }, [channels, query]);

  return (
    <div className="flex flex-col h-full bg-background" data-testid="mobile-channels-list">
      <MobileTopBar
        title="Canali"
        subtitle="Annunci e comunicazioni"
        leading={
          <div
            className="h-9 w-9 ml-1 rounded-xl flex items-center justify-center text-white shadow-sm"
            style={{ background: `linear-gradient(135deg, ${auroraPalette.indigo}, ${auroraPalette.violet})` }}
            aria-hidden
          >
            <Megaphone className="w-4 h-4" />
          </div>
        }
        actions={
          isAdmin
            ? [{
                id: 'create',
                label: 'Crea canale',
                icon: <Plus className="w-5 h-5" />,
                onClick: onCreateClick,
              }]
            : []
        }
      />

      <div
        className="sticky top-0 z-10 bg-background/95 backdrop-blur-md border-b border-border px-3 py-2"
      >
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          <Input
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="Cerca canale…"
            className="h-10 pl-9 rounded-full bg-muted/60 border-transparent focus-visible:border-border"
            data-testid="mobile-channels-search"
          />
        </div>
      </div>

      <div
        className="flex-1 overflow-y-auto overscroll-contain"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {loading && channels.length === 0 ? (
          <div className="p-3 space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 p-2">
                <div className="h-11 w-11 rounded-xl bg-muted animate-pulse" />
                <div className="flex-1 space-y-2">
                  <div className="h-3.5 w-1/3 bg-muted rounded animate-pulse" />
                  <div className="h-3 w-2/3 bg-muted/70 rounded animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-4 py-10" data-testid="mobile-channels-empty">
            <EmptyState
              illustration="no-posts"
              title={query ? 'Nessun canale corrisponde' : 'Nessun canale disponibile'}
              subtitle={
                query
                  ? 'Prova a cambiare i termini di ricerca.'
                  : isAdmin
                  ? 'Crea il primo canale per le comunicazioni aziendali.'
                  : 'Non ci sono canali a cui accedere al momento.'
              }
            />
          </div>
        ) : (
          <ul role="listbox" aria-label="Canali">
            {filtered.map((ch, index) => {
              const title = ch.title || 'Canale';
              const unread = ch.unread_count ?? 0;
              return (
                <motion.li
                  key={ch.id}
                  role="option"
                  initial={aurora.listItemIn.initial}
                  animate={aurora.listItemIn.animate}
                  transition={{ ...aurora.listItemIn.transition, delay: aurora.stagger(index) }}
                  className="border-b border-border/60 last:border-b-0"
                >
                  <button
                    type="button"
                    onClick={() => onSelect(ch)}
                    className="w-full flex items-center gap-3 px-3 py-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset"
                    style={{ outlineColor: auroraPalette.indigo }}
                    data-testid={`mobile-channel-item-${ch.id}`}
                  >
                    <div
                      className="h-11 w-11 rounded-xl flex items-center justify-center text-white shrink-0 shadow-sm"
                      style={{ background: gradientFor(title) }}
                      aria-hidden
                    >
                      <Megaphone className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-2">
                        <span
                          className={cn(
                            'truncate text-sm',
                            unread > 0 ? 'font-semibold text-foreground' : 'font-medium text-foreground',
                          )}
                        >
                          {title}
                          {ch.is_official && (
                            <span
                              className="ml-1.5 inline-flex items-center align-middle px-1.5 py-[1px] rounded-full text-[9px] font-semibold text-white"
                              style={{ background: auroraPalette.indigo }}
                              aria-label="Canale ufficiale"
                            >
                              UFFICIALE
                            </span>
                          )}
                        </span>
                        <span
                          className={cn(
                            aurora.type.caption,
                            unread > 0 ? 'text-foreground/80 font-medium' : 'text-muted-foreground',
                          )}
                        >
                          {formatRelative(ch.latest_post_at || ch.updated_at || ch.created_at)}
                        </span>
                      </div>
                      <div className="flex items-end justify-between gap-2 mt-0.5">
                        <span
                          className={cn(
                            'truncate text-xs',
                            unread > 0 ? 'text-foreground/80' : 'text-muted-foreground',
                          )}
                        >
                          {ch.last_post_preview || ch.description || 'Nessun annuncio ancora'}
                        </span>
                        {unread > 0 && (
                          <span
                            className="ml-2 min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold inline-flex items-center justify-center text-white"
                            style={{ background: auroraPalette.indigo }}
                            aria-label={`${unread} non letti`}
                          >
                            {unread > 99 ? '99+' : unread}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                </motion.li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Detail canale
// ─────────────────────────────────────────────────────────────────────────

interface ChannelDetailProps {
  channel: ChannelSummary;
  detail: ChannelDetail | null;
  posts: ChannelPost[];
  loadingPosts: boolean;
  canPost: boolean;
  isChannelAdmin: boolean;
  onBack: () => void;
  onSubscribeToggle: () => void;
  onOpenSettings: () => void;
  onPublish: (text: string, files: File[]) => Promise<void>;
  comments: Record<string, ChannelComment[]>;
  onLoadComments: (msgId: string) => void;
  onSubmitComment: (msgId: string, text: string) => Promise<void>;
  isSubscribed: boolean;
}

function ChannelDetailView({
  channel, detail, posts, loadingPosts, canPost, isChannelAdmin,
  onBack, onSubscribeToggle, onOpenSettings, onPublish, comments,
  onLoadComments, onSubmitComment, isSubscribed,
}: ChannelDetailProps) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [kbInset, setKbInset] = useState(0);
  const [postText, setPostText] = useState('');
  const [postFiles, setPostFiles] = useState<File[]>([]);
  const [publishing, setPublishing] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [commentDrafts, setCommentDrafts] = useState<Record<string, string>>({});
  const [submittingComment, setSubmittingComment] = useState<Record<string, boolean>>({});
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => {
      const layoutH = window.innerHeight;
      const visualH = vv.height;
      const delta = Math.max(0, layoutH - visualH - (vv.offsetTop || 0));
      setKbInset(delta);
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, []);

  const toggleComments = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
        if (!comments[id]) onLoadComments(id);
      }
      return next;
    });
  };

  const publish = async () => {
    if (publishing) return;
    if (!postText.trim() && postFiles.length === 0) return;
    setPublishing(true);
    try {
      await onPublish(postText.trim(), postFiles);
      setPostText('');
      setPostFiles([]);
    } finally {
      setPublishing(false);
    }
  };

  const submitComment = async (msgId: string) => {
    const text = commentDrafts[msgId]?.trim();
    if (!text) return;
    setSubmittingComment((p) => ({ ...p, [msgId]: true }));
    try {
      await onSubmitComment(msgId, text);
      setCommentDrafts((p) => ({ ...p, [msgId]: '' }));
    } finally {
      setSubmittingComment((p) => ({ ...p, [msgId]: false }));
    }
  };

  const title = channel.title || 'Canale';
  const memberCount = detail?.member_count ?? channel.member_count ?? 0;
  const subtitle = `${memberCount} iscritt${memberCount === 1 ? 'o' : 'i'}`;

  return (
    <div className="flex flex-col h-full bg-background relative" data-testid="mobile-channel-detail">
      <MobileTopBar
        title={title}
        subtitle={subtitle}
        onBack={onBack}
        leading={
          <div
            className="h-9 w-9 ml-1 rounded-xl flex items-center justify-center text-white shadow-sm"
            style={{ background: gradientFor(title) }}
            aria-hidden
          >
            <Megaphone className="w-4 h-4" />
          </div>
        }
        actions={[
          {
            id: 'subscribe',
            label: isSubscribed ? 'Disiscriviti' : 'Iscriviti',
            icon: isSubscribed ? <BellOff className="w-5 h-5" /> : <Bell className="w-5 h-5" />,
            onClick: onSubscribeToggle,
          },
          ...(isChannelAdmin
            ? [{
                id: 'settings',
                label: 'Impostazioni canale',
                icon: <Settings className="w-5 h-5" />,
                onClick: onOpenSettings,
              }]
            : []),
        ]}
      />

      {!canPost && (
        <div
          className="flex items-center gap-2 px-4 py-2 border-b border-border bg-muted/60"
          data-testid="mobile-channel-readonly"
        >
          <Eye className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          <span className={cn(aurora.type.caption, 'text-muted-foreground')}>
            Solo lettura — puoi solo leggere questo canale
          </span>
        </div>
      )}

      <div
        ref={scrollerRef}
        className="flex-1 overflow-y-auto overscroll-contain px-3 py-3"
        data-testid="mobile-channel-scroller"
      >
        {loadingPosts && posts.length === 0 ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin" style={{ color: auroraPalette.indigo }} />
          </div>
        ) : posts.length === 0 ? (
          <div className="py-8">
            <EmptyState
              illustration="no-posts"
              title="Nessun annuncio"
              subtitle={canPost
                ? 'Pubblica il primo annuncio in questo canale.'
                : 'Nessun annuncio pubblicato in questo canale al momento.'}
            />
          </div>
        ) : (
          <div className="space-y-3">
            {posts.map((post, index) => {
              const author = [post.sender_first_name, post.sender_last_name].filter(Boolean).join(' ') || 'Utente';
              const atts = parseAttachments(post.attachments);
              const cList = comments[post.id] || [];
              const isOpen = expanded.has(post.id);
              return (
                <motion.article
                  key={post.id}
                  initial={aurora.listItemIn.initial}
                  animate={aurora.listItemIn.animate}
                  transition={{ ...aurora.listItemIn.transition, delay: aurora.stagger(Math.min(index, 12)) }}
                  className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden"
                  data-testid={`mobile-channel-post-${post.id}`}
                >
                  <div className="p-3">
                    <div className="flex items-center gap-2.5 mb-2">
                      {post.sender_avatar_url ? (
                        <img
                          src={post.sender_avatar_url}
                          alt=""
                          className="h-9 w-9 rounded-full object-cover bg-muted"
                          loading="lazy"
                        />
                      ) : (
                        <div
                          className="h-9 w-9 rounded-full flex items-center justify-center text-xs font-semibold text-white"
                          style={{ background: gradientFor(author) }}
                          aria-hidden
                        >
                          {initialsFor(author)}
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-foreground truncate">{author}</div>
                        <div className={cn(aurora.type.caption, 'text-muted-foreground')}>
                          {formatRelative(post.created_at)}
                        </div>
                      </div>
                    </div>

                    {post.content && (
                      <p className="text-sm whitespace-pre-wrap leading-relaxed text-foreground">{post.content}</p>
                    )}

                    {atts.length > 0 && (
                      <div className="mt-2 space-y-1.5">
                        {atts.map((a, i) => {
                          const url = attachmentUrl(a);
                          const mime = a.mime_type || a.type || '';
                          const filename = a.filename || a.name || 'File';
                          if (mime.startsWith('image/') && url) {
                            return (
                              <a key={i} href={url} target="_blank" rel="noreferrer" className="block rounded-lg overflow-hidden">
                                <img src={url} alt={filename} className="max-w-full max-h-72 object-cover" loading="lazy" />
                              </a>
                            );
                          }
                          return (
                            <a
                              key={i}
                              href={url || '#'}
                              target="_blank"
                              rel="noreferrer"
                              className="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-muted text-xs text-foreground hover:bg-muted/80"
                            >
                              <Paperclip className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                              <span className="truncate">{filename}</span>
                            </a>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="border-t border-border px-3 py-2">
                    <button
                      type="button"
                      onClick={() => toggleComments(post.id)}
                      className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                      data-testid={`mobile-channel-toggle-comments-${post.id}`}
                    >
                      <MessageSquare className="w-3.5 h-3.5" />
                      {cList.length} commenti
                      <ChevronDown className={cn('w-3 h-3 transition-transform', isOpen && 'rotate-180')} />
                    </button>
                  </div>

                  {isOpen && (
                    <div className="border-t border-border bg-muted/40 px-3 py-2.5 space-y-2.5">
                      {cList.map((c) => {
                        const cName = [c.first_name, c.last_name].filter(Boolean).join(' ') || 'Utente';
                        return (
                          <div key={c.id} className="flex items-start gap-2">
                            {c.avatar_url ? (
                              <img src={c.avatar_url} alt="" className="h-7 w-7 rounded-full object-cover bg-background" loading="lazy" />
                            ) : (
                              <div
                                className="h-7 w-7 rounded-full flex items-center justify-center text-[10px] font-semibold text-white shrink-0"
                                style={{ background: gradientFor(cName) }}
                                aria-hidden
                              >
                                {initialsFor(cName)}
                              </div>
                            )}
                            <div className="flex-1 min-w-0 rounded-xl bg-background px-2.5 py-1.5 border border-border">
                              <div className="flex items-baseline gap-2">
                                <span className="text-xs font-semibold text-foreground truncate">{cName}</span>
                                <span className="text-[10px] text-muted-foreground">{formatRelative(c.created_at)}</span>
                              </div>
                              {c.content && <p className="text-xs text-foreground mt-0.5 whitespace-pre-wrap">{c.content}</p>}
                            </div>
                          </div>
                        );
                      })}

                      {isSubscribed && (
                        <div className="flex items-center gap-2">
                          <Input
                            value={commentDrafts[post.id] || ''}
                            onChange={(e) => setCommentDrafts((p) => ({ ...p, [post.id]: e.target.value }))}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                void submitComment(post.id);
                              }
                            }}
                            placeholder="Scrivi un commento…"
                            className="h-9 rounded-full bg-background text-xs"
                            data-testid={`mobile-channel-comment-input-${post.id}`}
                          />
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            disabled={!commentDrafts[post.id]?.trim() || submittingComment[post.id]}
                            onClick={() => void submitComment(post.id)}
                            className="h-9 w-9 rounded-full shrink-0"
                            aria-label="Invia commento"
                          >
                            {submittingComment[post.id]
                              ? <Loader2 className="w-4 h-4 animate-spin" />
                              : <Send className="w-4 h-4" />}
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </motion.article>
              );
            })}
          </div>
        )}
      </div>

      {canPost && (
        <div
          className="sticky bottom-0 z-10 border-t border-border bg-background/95 backdrop-blur-md px-2 py-2"
          style={{ paddingBottom: `calc(env(safe-area-inset-bottom) + ${kbInset}px)` }}
          data-testid="mobile-channel-composer"
        >
          {postFiles.length > 0 && (
            <div className="flex flex-wrap gap-1.5 px-1 pb-2">
              {postFiles.map((f, i) => (
                <span key={i} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-muted text-[11px] text-foreground">
                  <Paperclip className="w-3 h-3" />
                  <span className="truncate max-w-[140px]">{f.name}</span>
                  <button
                    type="button"
                    onClick={() => setPostFiles((prev) => prev.filter((_, j) => j !== i))}
                    aria-label="Rimuovi allegato"
                    className="hover:text-destructive"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
          <div className="flex items-end gap-2">
            <input
              ref={fileRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                if (e.target.files) setPostFiles((prev) => [...prev, ...Array.from(e.target.files as FileList)]);
                e.target.value = '';
              }}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => fileRef.current?.click()}
              disabled={publishing}
              className="h-11 w-11 rounded-full shrink-0"
              aria-label="Allega file"
            >
              <Paperclip className="w-5 h-5" />
            </Button>
            <Textarea
              value={postText}
              onChange={(e) => setPostText(e.target.value)}
              placeholder="Pubblica un annuncio…"
              rows={1}
              className="min-h-[44px] max-h-32 resize-none rounded-2xl bg-muted/60 border-transparent focus-visible:border-border text-sm"
              data-testid="mobile-channel-composer-text"
            />
            <Button
              type="button"
              size="icon"
              onClick={() => void publish()}
              disabled={publishing || (!postText.trim() && postFiles.length === 0)}
              className="h-11 w-11 rounded-full shrink-0"
              style={{ background: auroraPalette.indigo }}
              aria-label="Pubblica"
              data-testid="mobile-channel-publish"
            >
              {publishing
                ? <Loader2 className="w-4 h-4 animate-spin text-white" />
                : <Send className="w-4 h-4 text-white" />}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Create channel sheet (admin only)
// ─────────────────────────────────────────────────────────────────────────

interface CreateSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreate: (input: { name: string; description: string; isPublic: boolean }) => Promise<void>;
}

function CreateChannelSheet({ open, onOpenChange, onCreate }: CreateSheetProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setName('');
      setDescription('');
      setIsPublic(true);
      setBusy(false);
    }
  }, [open]);

  const submit = async () => {
    if (!name.trim() || busy) return;
    setBusy(true);
    try {
      await onCreate({ name: name.trim(), description: description.trim(), isPublic });
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-2xl p-0">
        <SheetHeader className="px-4 pt-4 pb-2">
          <SheetTitle>Nuovo canale</SheetTitle>
        </SheetHeader>
        <div className="px-4 pb-4 space-y-3">
          <div>
            <Label className="text-xs">Nome del canale</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="es. Comunicazioni Direzione"
              className="mt-1"
              autoFocus
            />
          </div>
          <div>
            <Label className="text-xs">Descrizione</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descrizione del canale…"
              rows={3}
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs">Visibilità</Label>
            <div className="flex gap-2 mt-1">
              <Button
                type="button"
                size="sm"
                variant={isPublic ? 'default' : 'outline'}
                onClick={() => setIsPublic(true)}
                className="rounded-full gap-1.5"
              >
                <Hash className="w-3.5 h-3.5" />Pubblico
              </Button>
              <Button
                type="button"
                size="sm"
                variant={!isPublic ? 'default' : 'outline'}
                onClick={() => setIsPublic(false)}
                className="rounded-full gap-1.5"
              >
                <Megaphone className="w-3.5 h-3.5" />Privato
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1.5">
              {isPublic
                ? 'Tutti possono vedere e iscriversi al canale.'
                : 'Solo i membri invitati possono accedere.'}
            </p>
          </div>
        </div>
        <SheetFooter className="px-4 pb-[calc(env(safe-area-inset-bottom)+12px)] gap-2 flex-row">
          <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)} disabled={busy}>
            Annulla
          </Button>
          <Button
            className="flex-1"
            onClick={() => void submit()}
            disabled={!name.trim() || busy}
            style={{ background: auroraPalette.indigo }}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Crea'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Page entry
// ─────────────────────────────────────────────────────────────────────────

export default function MobileChannels() {
  const { user } = useAuth();
  const nav = usePushNavigation<View>({ initial: 'list' });

  const isTenantAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const currentUserId = user?.id ?? null;

  const [channels, setChannels] = useState<ChannelSummary[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<ChannelSummary | null>(null);
  const [detail, setDetail] = useState<ChannelDetail | null>(null);
  const [posts, setPosts] = useState<ChannelPost[]>([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [comments, setComments] = useState<Record<string, ChannelComment[]>>({});
  const [createOpen, setCreateOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const loadChannels = useCallback(async () => {
    setLoadingList(true);
    try {
      const res: ApiResponse<ChannelSummary[]> = await apiClient.get<ChannelSummary[]>('/messenger/channels');
      if (res.success && Array.isArray(res.data)) setChannels(res.data);
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => { void loadChannels(); }, [loadChannels]);

  const loadDetail = useCallback(async (chId: string) => {
    const res: ApiResponse<ChannelDetail> = await apiClient.get<ChannelDetail>(`/messenger/conversations/${chId}`);
    if (res.success && res.data) setDetail(res.data);
  }, []);

  const loadPosts = useCallback(async (chId: string) => {
    setLoadingPosts(true);
    try {
      const res: ApiResponse<ChannelPost[]> = await apiClient.get<ChannelPost[]>(`/messenger/conversations/${chId}/messages`);
      if (res.success && Array.isArray(res.data)) setPosts([...res.data].reverse());
    } finally {
      setLoadingPosts(false);
    }
  }, []);

  const openChannel = useCallback(async (ch: ChannelSummary) => {
    setSelected(ch);
    setDetail(null);
    setPosts([]);
    setComments({});
    nav.push('detail');
    await Promise.all([loadDetail(ch.id), loadPosts(ch.id)]);
  }, [nav, loadDetail, loadPosts]);

  const backToList = useCallback(() => {
    nav.pop();
    setSelected(null);
    setDetail(null);
    setPosts([]);
    setComments({});
  }, [nav]);

  const toggleSubscription = useCallback(async () => {
    if (!selected) return;
    const subscribed = detail?.is_subscribed ?? selected.is_subscribed ?? false;
    const method = subscribed ? 'delete' : 'post';
    if (method === 'delete') {
      await apiClient.delete(`/messenger/conversations/${selected.id}/subscribe`);
    } else {
      await apiClient.post(`/messenger/conversations/${selected.id}/subscribe`, {});
    }
    await Promise.all([loadChannels(), loadDetail(selected.id)]);
  }, [selected, detail, loadChannels, loadDetail]);

  const publish = useCallback(async (content: string, files: File[]) => {
    if (!selected) return;
    if (files.length > 0) {
      const fd = new FormData();
      if (content) fd.append('content', content);
      files.forEach((f) => fd.append('files', f));
      await apiClient.post(`/messenger/conversations/${selected.id}/messages`, fd);
    } else {
      await apiClient.post(`/messenger/conversations/${selected.id}/messages`, { content });
    }
    await loadPosts(selected.id);
  }, [selected, loadPosts]);

  const loadComments = useCallback(async (msgId: string) => {
    const res: ApiResponse<ChannelComment[]> = await apiClient.get<ChannelComment[]>(`/messenger/messages/${msgId}/comments`);
    if (res.success && Array.isArray(res.data)) {
      setComments((p) => ({ ...p, [msgId]: res.data ?? [] }));
    }
  }, []);

  const submitComment = useCallback(async (msgId: string, text: string) => {
    await apiClient.post(`/messenger/messages/${msgId}/comments`, { content: text });
    await loadComments(msgId);
  }, [loadComments]);

  const createChannel = useCallback(async (input: { name: string; description: string; isPublic: boolean }) => {
    await apiClient.post('/messenger/conversations', {
      type: 'channel',
      title: input.name,
      description: input.description,
      is_public: input.isPublic,
    });
    await loadChannels();
  }, [loadChannels]);

  const isSubscribed = !!(detail?.is_subscribed ?? selected?.is_subscribed);
  const isChannelAdmin = detail?.my_role === 'admin'
    || detail?.my_role === 'publisher'
    || isTenantAdmin
    || (currentUserId !== null && selected?.created_by === currentUserId);
  const canPost = !!(isSubscribed && isChannelAdmin);

  return (
    <div className="fixed inset-0 z-30 bg-background" data-testid="mobile-channels-shell">
      {nav.view === 'detail' && selected ? (
        <ChannelDetailView
          channel={selected}
          detail={detail}
          posts={posts}
          loadingPosts={loadingPosts}
          canPost={canPost}
          isChannelAdmin={isChannelAdmin}
          isSubscribed={isSubscribed}
          onBack={backToList}
          onSubscribeToggle={() => void toggleSubscription()}
          onOpenSettings={() => setSettingsOpen(true)}
          onPublish={publish}
          comments={comments}
          onLoadComments={loadComments}
          onSubmitComment={submitComment}
        />
      ) : (
        <ChannelListView
          channels={channels}
          loading={loadingList}
          query={query}
          onQueryChange={setQuery}
          onSelect={(ch) => void openChannel(ch)}
          isAdmin={isTenantAdmin}
          onCreateClick={() => setCreateOpen(true)}
        />
      )}

      <CreateChannelSheet open={createOpen} onOpenChange={setCreateOpen} onCreate={createChannel} />
      <ChannelSettingsSheet
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        channel={selected}
        detail={detail}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Settings sheet (admin only) — info canale + azione "Disiscrivi tutti i
// silenziosi" non disponibile lato backend, quindi sheet read-only con
// metadati canale (nome, descrizione, visibilità, iscritti, creazione).
// Estendibile (vedi follow-up #1737 per gestione membri).
// ─────────────────────────────────────────────────────────────────────────

interface SettingsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  channel: ChannelSummary | null;
  detail: ChannelDetail | null;
}

function ChannelSettingsSheet({ open, onOpenChange, channel, detail }: SettingsSheetProps) {
  if (!channel) return null;
  const merged: ChannelDetail = { ...channel, ...(detail ?? {}) };
  const memberCount = merged.member_count ?? 0;
  const visibility = merged.is_public === false ? 'Privato' : 'Pubblico';
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-2xl p-0">
        <SheetHeader className="px-4 pt-4 pb-2">
          <SheetTitle className="flex items-center gap-2">
            <Settings className="w-4 h-4" />
            Impostazioni canale
          </SheetTitle>
        </SheetHeader>
        <div className="px-4 pb-4 space-y-3">
          <div className="rounded-xl border border-border bg-muted/40 px-3 py-2.5">
            <div className="text-[11px] text-muted-foreground">Nome</div>
            <div className="text-sm font-semibold text-foreground truncate">{merged.title || 'Canale'}</div>
          </div>
          {merged.description && (
            <div className="rounded-xl border border-border bg-muted/40 px-3 py-2.5">
              <div className="text-[11px] text-muted-foreground">Descrizione</div>
              <div className="text-sm text-foreground whitespace-pre-wrap">{merged.description}</div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-xl border border-border bg-muted/40 px-3 py-2.5">
              <div className="text-[11px] text-muted-foreground flex items-center gap-1">
                <Users className="w-3 h-3" />Iscritti
              </div>
              <div className="text-sm font-semibold text-foreground">{memberCount}</div>
            </div>
            <div className="rounded-xl border border-border bg-muted/40 px-3 py-2.5">
              <div className="text-[11px] text-muted-foreground flex items-center gap-1">
                <Hash className="w-3 h-3" />Visibilità
              </div>
              <div className="text-sm font-semibold text-foreground">{visibility}</div>
            </div>
          </div>
          {merged.created_at && (
            <div className="rounded-xl border border-border bg-muted/40 px-3 py-2.5">
              <div className="text-[11px] text-muted-foreground">Creato</div>
              <div className="text-sm text-foreground">{formatRelative(merged.created_at)}</div>
            </div>
          )}
          <div className="flex items-start gap-2 px-1 pt-1 text-[11px] text-muted-foreground">
            <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span>
              La gestione membri (aggiungi, rimuovi, promuovi a publisher) è in arrivo
              — usa l'esperienza desktop per intervenire ora.
            </span>
          </div>
        </div>
        <SheetFooter className="px-4 pb-[calc(env(safe-area-inset-bottom)+12px)]">
          <Button variant="outline" className="w-full" onClick={() => onOpenChange(false)}>
            Chiudi
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
